import { useParams, Link, Navigate } from "react-router-dom";
import { siteConfig } from "@/mocks/site";
import { articles } from "@/mocks/articles";
import ScrollToTop from "@/components/base/ScrollToTop";
import PageMeta from "@/components/base/PageMeta";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);

  const article = articles.find((a) => a.id === articleId);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const relatedArticles = articles.filter(
    (a) => article.relatedArticles?.includes(a.id)
  );

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.date,
    author: {
      "@type": "Organization",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: siteConfig.name,
      url: siteConfig.url,
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${siteConfig.url}/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: siteConfig.url },
      { "@type": "ListItem", position: 2, name: "文章", item: `${siteConfig.url}/` },
      { "@type": "ListItem", position: 3, name: article.title },
    ],
  };

  return (
    <>
      <PageMeta
        title={`${article.title} - 蜜桃影视`}
        description={article.excerpt}
        keywords={article.tags.join(",")}
        canonicalUrl={`${siteConfig.url}/article/${article.id}`}
        schemaObjects={[articleSchema, breadcrumbSchema]}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <Breadcrumb
            items={[
              { label: "首页", path: "/" },
              { label: "文章" },
              { label: article.title },
            ]}
          />
        </div>

        <article className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-12">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap">
                {article.category}
              </span>
              <span className="text-xs text-foreground-500 whitespace-nowrap">{article.date}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span className="text-xs text-foreground-500 whitespace-nowrap">{article.readTime} 分钟阅读</span>
            </div>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-2 mb-6 flex-wrap">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 rounded text-xs bg-background-100 text-foreground-600 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="aspect-[16/9] rounded-xl overflow-hidden bg-background-200 mb-8">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground-950 prose-p:text-foreground-700 prose-p:leading-relaxed prose-h2:text-xl md:prose-h2:text-2xl prose-h3:text-lg md:prose-h3:text-xl prose-a:text-primary-600"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200">
              <p className="text-sm text-foreground-500 mb-3">
                作者：<strong className="text-foreground-800">{article.author}</strong>
                &nbsp;|&nbsp; 发布日期：{article.date}
              </p>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 lg:px-8 py-12 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} {...ra} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}